/**
 * Client for the local Device Service — the customer's already-installed Java app that drives the
 * USB fingerprint scanners and builds the EBTS/EFT binary. Ported from the Angular app's
 * DeviceServiceUrlService + DeviceApiService + FrictionRidgeCaptureDevicesService + CaptureService.
 *
 * Discovery: probe GET /version on the loopback address, ONE request at a time, and cache what answers.
 * The service is reached over plain http on loopback; no new software is installed on the workstation.
 */

import { permissionHelp } from './permissionHelp';

/** Where the Device Service listens unless this user has said otherwise. */
export const DEFAULT_DEVICE_PORT = 8080;

/**
 * The port to expect the Device Service on — configuration, not a search.
 *
 * Discovery used to sweep 8080-8089 and 9080-9089 across three hostnames: sixty requests, on every connect,
 * to find one service that is on 8080 for virtually everyone. It became unworkable once Local Network Access
 * made the first request wait on a permission dialog. So the port is now a setting on the user's profile,
 * applied here by `configureDevicePort` when the profile loads, and the rare workstation that had to move off
 * 8080 finds it once via `scanForDeviceService` and stores the answer.
 *
 * Module state rather than a parameter because DeviceService.connect() is called from several screens that
 * have no business knowing about it. The default keeps everything working before the profile has loaded.
 */
let configuredPort = Number(import.meta.env.VITE_DEVICE_PORT ?? DEFAULT_DEVICE_PORT) || DEFAULT_DEVICE_PORT;

export function configureDevicePort(port: number | null | undefined): void {
  const next = port && Number.isInteger(port) ? port : DEFAULT_DEVICE_PORT;
  // A changed port invalidates the cached base — it points at the old one.
  if (next !== configuredPort) cachedBase = undefined;
  configuredPort = next;
}

export function deviceServicePort(): number {
  return configuredPort;
}

/**
 * Whether the Device Service has already been located this session.
 *
 * Lets a caller gather diagnostics from an established connection without starting a discovery that could
 * raise a local-network permission dialog — which would be an unpleasant surprise on, say, a support form.
 */
export function deviceServiceDiscovered(): boolean {
  return Boolean(cachedBase);
}

/** Ports a stray install might be on, for the on-demand scan only. Never used by a normal connect. */
const SCAN_PORTS = [
  ...Array.from({ length: 10 }, (_, i) => 8080 + i),
  ...Array.from({ length: 10 }, (_, i) => 9080 + i),
];

// Optional HTTPS host: a public hostname that resolves to 127.0.0.1 with a trusted TLS cert, for browsers
// that block http://localhost from an HTTPS page (Safari/Firefox). Only used if the customer runs the
// Device Service over HTTPS. Overridable via VITE_DEVICE_HOST.
const HTTPS_DEVICE_HOST = import.meta.env.VITE_DEVICE_HOST ?? 'device.eftcreator.com';

// Every Device Service request carries these: the api-key authorizes the call and identifies the
// EFTCreator product to the service; the api-url tells it which registry to validate against. URL is
// per-environment (set at build time); the key is a product identifier (not a secret). Defaults cover
// local `npm run dev`.
// Defaults are for local `npm run dev` only (deployed builds get these from the pipeline / .env):
// the DEV key paired with the DEV registry. Deployed dev/prod inject env-specific values.
const DEVSVC_API_KEY = import.meta.env.VITE_DEVSVC_API_KEY ?? '70ec4808-6278-4944-9601-907f26ea4da9';
const DEVSVC_API_URL = import.meta.env.VITE_DEVSVC_API_URL ?? 'https://registry-dev.lakotasoftware.com';
const DEVSVC_HEADERS: Record<string, string> = {
  'devsvc-api-key': DEVSVC_API_KEY,
  'devsvc-api-url': DEVSVC_API_URL,
};

/**
 * Local Network Access: every request to the Device Service declares that it targets a loopback address.
 *
 * Browsers implementing LNA (Firefox first) gate a public page's requests to 127.0.0.1 behind a permission,
 * and will not consider granting it unless the request says where it is going. Without this declaration the
 * call is refused before the user is ever asked — which is the bulk of the "cannot reach the Device Service"
 * reports from Firefox. It must be on EVERY call, not just discovery: the permission is checked per request,
 * so a probe that succeeds followed by a capture that omits it fails at the capture.
 *
 * `targetAddressSpace` is not in the DOM types yet, and browsers that do not implement it ignore unknown
 * fetch init keys — so this is inert on Chrome, Edge and Safari rather than something to feature-detect.
 */
type LnaRequestInit = RequestInit & { targetAddressSpace?: 'loopback' | 'local' | 'public' };

/** fetch() for the Device Service. The one place the loopback declaration is added, so it cannot be missed. */
function dsFetch(url: string, init: LnaRequestInit = {}): Promise<Response> {
  return fetch(url, { ...init, targetAddressSpace: 'loopback' } as RequestInit);
}

/**
 * Whether this browser will let the page reach loopback, as the browser itself reports it.
 *
 * `loopback-network` is unknown to browsers without LNA, where `query` rejects — that is "no gate here",
 * not "denied", so it resolves to `unsupported` and callers must treat the two differently. Guessing at the
 * state is what produced the old advice: every failure was reported as a CORS/PNA misconfiguration on the
 * customer's Device Service, when for Firefox users it was a permission they had never been asked for.
 */
export type LoopbackPermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

/**
 * The permission names browsers use for this, newest first.
 *
 * `loopback-network` is the granular name in the current specification. `local-network-access` is the
 * original unified permission, kept as an alias — and it is the name CHROMIUM shipped the prompt under in
 * Chrome 142. Querying only the first is why Edge and Chrome reported `unsupported` while the browser was
 * in fact gating the request: the query rejected, we concluded "no gate here", budgeted 3s for a request
 * that was sitting on a permission dialog, and then reported the failure as the customer's Device Service
 * refusing CORS. Both names are tried so the answer comes from whichever the browser understands.
 */
const LOOPBACK_PERMISSIONS = ['loopback-network', 'local-network-access'] as const;

export async function loopbackPermission(): Promise<LoopbackPermission> {
  for (const name of LOOPBACK_PERMISSIONS) {
    try {
      const status = await navigator.permissions.query({ name: name as PermissionName });
      return status.state as LoopbackPermission;
    } catch {
      // Unknown to this browser — try the other spelling before concluding there is no gate.
    }
  }
  return 'unsupported';
}

/*
 * Discovery budgets.
 *
 * The old 600ms applied to a request that can now sit pending while the USER decides whether to allow local
 * network access. The grant then arrived with nothing left to receive, so the customer clicked Allow and the
 * page still said the service was unreachable. A prompt-gated first pass needs tens of seconds; once the
 * permission is granted the answer comes back at loopback speed and a short budget is right again.
 */
const PROBE_MS_GRANTED = 1_500;
const PROBE_MS_PROMPT = 30_000;
/** No LNA implementation to wait on, but 600ms was tight even for a busy workstation. */
const PROBE_MS_UNSUPPORTED = 3_000;

let cachedBase: string | undefined;
/** Whether the discovered install authenticates /version. Defaults to true — that's what the Device
 *  Service builds in the field today do, and it's the safe assumption if discovery hasn't run. */
let cachedVersionNeedsHeaders = true;

/**
 * Why the last discovery attempt failed, per base that actually answered. Ports with nothing listening
 * are omitted — they're noise. Surfaced in the thrown errors because the previous code collapsed a 401,
 * a rejected CORS preflight and a closed port into the same silent `false`, which is precisely why this
 * failure mode has been diagnosed (and re-diagnosed) from guesswork.
 */
let probeNotes: string[] = [];
export function lastDeviceProbeNotes(): string[] {
  return [...probeNotes];
}

interface ProbeResult { ok: boolean; status?: number }

async function fetchVersion(base: string, withHeaders: boolean, timeoutMs: number): Promise<ProbeResult> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await dsFetch(`${base}/version`, {
      ...(withHeaders ? { headers: DEVSVC_HEADERS } : {}),
      signal: ctrl.signal,
    });
    return { ok: res.ok, status: res.status };
  } catch {
    // Indistinguishable from here: nothing listening, or the browser rejected the response/preflight.
    return { ok: false };
  } finally {
    clearTimeout(t);
  }
}

/**
 * Probe one candidate base, tolerating either /version contract.
 *
 * The devsvc headers go on FIRST: the Device Service's LicenseInterceptor authenticates /version on the
 * builds customers are running today, so a header-free probe just earns a 401. Sending them stays
 * correct on a future build that drops the requirement, since unused headers are simply ignored.
 *
 * Their only cost is that non-safelisted headers force a CORS preflight. So if that attempt fails
 * without ever getting a status — nothing listening, or a refused preflight — we retry header-free.
 * That's a simple request with no preflight, which still discovers a build that no longer authenticates
 * /version even when the service refuses the preflight for the custom headers.
 */
async function probeBase(base: string, timeoutMs: number): Promise<boolean> {
  const authed = await fetchVersion(base, true, timeoutMs);
  if (authed.ok) {
    cachedVersionNeedsHeaders = true;
    return true;
  }

  /*
   * The header-free retry never waits on a permission dialog, whatever the first attempt was allowed.
   *
   * By the time it runs, the request that could raise the prompt has already been made — the customer has
   * been asked, or never will be. Giving this one the same tens-of-seconds budget doubled the time before
   * an ignored dialog could be reported, for a retry that is about CORS headers rather than permission.
   */
  const plain = await fetchVersion(base, false, Math.min(timeoutMs, PROBE_MS_GRANTED));
  if (plain.ok) {
    cachedVersionNeedsHeaders = false;
    return true;
  }

  if (authed.status) {
    probeNotes.push(
      `${base}/version returned HTTP ${authed.status} with the devsvc-api-key/-url headers — the API key likely doesn't match the registry this Device Service validates against.`,
    );
  } else if (plain.status) {
    probeNotes.push(
      `${base}/version answered (HTTP ${plain.status}) without the devsvc headers, but the header-bearing request never completed — the Device Service must allow "devsvc-api-key" and "devsvc-api-url" in Access-Control-Allow-Headers for this origin, otherwise the CORS preflight is refused and the authenticated request is never sent.`,
    );
  }
  return false;
}

/** Opaque (no-cors) probe — resolves if the service is reachable at all, even when the CORS/PNA
 *  response is rejected. no-cors can't send the devsvc headers (they'd be dropped) and can't read the
 *  status, but a reachable host still completes the request (even a 401), which is all we need here. */
async function probeReachable(base: string, timeoutMs = PROBE_MS_GRANTED): Promise<boolean> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    await dsFetch(`${base}/version`, { mode: 'no-cors', signal: ctrl.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/**
 * The addresses to try, in order, all on the one port.
 *
 * 127.0.0.1 first because it is exactly what installs in the field use; `localhost` after it, since a
 * browser can resolve or gate the two differently; the HTTPS host last, for a workstation running the
 * Device Service behind a trusted cert (Safari/Firefox mixed-content).
 */
function candidateBases(port = configuredPort): string[] {
  return [
    `http://127.0.0.1:${port}`,
    `http://localhost:${port}`,
    `https://${HTTPS_DEVICE_HOST}:${port}`,
  ];
}

/**
 * The time to allow one probe, given what the browser has said about local network access.
 *
 * `prompt` means the request will sit pending while a human reads a dialog and decides — that needs tens of
 * seconds, not the 600ms this used to allow, which aborted the very request the grant was arriving for.
 */
function budgetFor(permission: LoopbackPermission): number {
  return permission === 'granted' ? PROBE_MS_GRANTED
    : permission === 'unsupported' ? PROBE_MS_UNSUPPORTED
    : PROBE_MS_PROMPT;
}

/**
 * Find and cache the Device Service base URL, or throw a specific error explaining why not.
 *
 * One request at a time. The first is the one that may raise the permission prompt, so it gets the full
 * budget; once the browser has answered, the remaining addresses are quick to rule out.
 */
export async function discoverBaseUrl(): Promise<string> {
  if (cachedBase) return cachedBase;
  probeNotes = [];

  let permission = await loopbackPermission();
  let budget = budgetFor(permission);

  for (const base of candidateBases()) {
    if (await probeBase(base, budget)) {
      cachedBase = base;
      return cachedBase;
    }
    /*
     * Re-read the permission after the request that could have raised the prompt.
     *
     * Granted: the remaining addresses answer at loopback speed, so stop budgeting for a human. Denied:
     * nothing will answer, and continuing only delays telling the operator. Still `prompt` means the dialog
     * was dismissed — also nothing to wait for.
     */
    if (permission === 'prompt') {
      permission = await loopbackPermission();
      if (permission === 'denied') throw new DeviceServicePermissionError('denied');
      budget = permission === 'prompt' ? PROBE_MS_GRANTED : budgetFor(permission);
    }
  }

  /*
   * Nothing answered on the one port we support, so the browser's own answer is the diagnosis.
   *
   * Checked before CORS/PNA: reporting a permission the customer was never asked for as a misconfiguration
   * of their Device Service is what sent Firefox users to change settings on software that was working.
   */
  if (permission === 'denied' || permission === 'prompt') {
    throw new DeviceServicePermissionError(permission);
  }

  // Reachable (no-cors) but the response was rejected — now genuinely a CORS/PNA question, since the
  // browser has told us it is not the one refusing.
  for (const base of candidateBases()) {
    if (await probeReachable(base, budget)) throw new DeviceServiceBlockedError(base, probeNotes);
  }

  // Nothing found. On an HTTPS page this is often the browser blocking http://localhost (Safari/Firefox).
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    throw new DeviceServiceInsecureError();
  }
  throw new DeviceServiceUnavailableError();
}

/**
 * Hunt for a Device Service on a non-default port, on request only.
 *
 * This is the escape hatch the removed sweep used to be, moved somewhere it belongs: behind a button the
 * customer presses when nothing was found, rather than run on every connect for everyone. Sequential and
 * quiet about it — by the time anyone reaches this, the local-network permission has already been settled by
 * the failed connect, so each attempt gets the short budget.
 *
 * Returns the port so the caller can store it on the profile; a workstation should be asked this once.
 */
export async function scanForDeviceService(
  onProgress?: (port: number, index: number, total: number) => void,
): Promise<number | null> {
  const ports = [configuredPort, ...SCAN_PORTS.filter((p) => p !== configuredPort)];
  for (const [i, port] of ports.entries()) {
    onProgress?.(port, i, ports.length);
    for (const base of candidateBases(port)) {
      if (await probeBase(base, PROBE_MS_GRANTED)) {
        cachedBase = base;
        configuredPort = port;
        return port;
      }
    }
  }
  return null;
}

export function wsBaseUrl(base: string): string {
  return base.replace(/^http/, 'ws') + '/ws';
}

export class DeviceServiceUnavailableError extends Error {
  constructor() {
    super('The local Device Service is not reachable. Ensure it is running on this workstation.');
    this.name = 'DeviceServiceUnavailableError';
  }
}

/**
 * This page is HTTPS but no HTTPS Device Service was found. Plain-http calls to the device are blocked
 * by the browser as mixed content (Safari/Firefox always; Chrome/Edge exempt only http://localhost).
 * Fix: serve the Device Service over HTTPS (trusted cert on a loopback-resolving hostname), or use
 * Chrome/Edge for capture, or run the app over http for local testing.
 */
export class DeviceServiceInsecureError extends Error {
  constructor() {
    super(
      'Could not reach the Device Service. Ensure it is running. Note: Chrome/Edge allow this HTTPS page to ' +
        'call the HTTP service on localhost, but Safari/Firefox block it — use Chrome/Edge, or run the ' +
        'Device Service over HTTPS.',
    );
    this.name = 'DeviceServiceInsecureError';
  }
}

/**
 * The browser will not let this page reach loopback, and says so.
 *
 * Distinct from DeviceServiceBlockedError, which is about the Device Service's own CORS configuration. This
 * one is the browser's permission, fixed by the customer in their browser — and it was being reported as the
 * former, which is why customers were told to reconfigure software that was behaving correctly.
 */
export class DeviceServicePermissionError extends Error {
  constructor(readonly state: 'prompt' | 'denied') {
    /*
     * The steps name the control THIS browser shows. Naming Firefox's was safe while Firefox was the only
     * implementation; Chromium shipped the prompt in Chrome 142, so most people reading this are now being
     * sent to look for a padlock setting their browser does not have.
     */
    const help = permissionHelp(typeof navigator === 'undefined' ? '' : navigator.userAgent, state);
    super(
      (state === 'denied'
        ? 'This browser is blocking EFTCreator from reaching the Device Service on this computer. '
        : 'This browser needs your permission to reach the Device Service on this computer. ') +
        help.steps.join(' '),
    );
    this.name = 'DeviceServicePermissionError';
  }
}

/**
 * The service answered an opaque probe but not a readable request.
 *
 * Which is a CORS question — the Device Service's own configuration — and NOT a Private Network Access one.
 * This used to tell the customer the service "must return Access-Control-Allow-Private-Network: true":
 * a header from the retired PNA design that no current browser consults, replaced by the local-network
 * PERMISSION. Worse, it asked the operator to change a response header in software they cannot edit, so the
 * only thing they could do with it was forward it to us as a Device Service bug — which is exactly what
 * happened. The notes carry the real detail (a 401, a refused preflight, a mismatched API key); the message
 * says who can act and how.
 */
export class DeviceServiceBlockedError extends Error {
  constructor(readonly base: string, readonly notes: string[] = []) {
    super(
      `The Device Service answered at ${base}, but this page could not read its response. ` +
        'Restart the Device Service and reload; if it persists, this needs Lakota support — the details are ' +
        'on Help > Device status.' +
        // The per-base detail is what makes this actionable rather than a shrug.
        (notes.length ? ` ${notes.join(' ')}` : ''),
    );
    this.name = 'DeviceServiceBlockedError';
  }
}

/**
 * Auth headers so the Device Service can call back to the cloud for validation/licensing,
 * mirroring the legacy eft-* headers.
 */
export interface CloudContext {
  region: string;
  endpoint: string;
  issuer: string;
  identityId?: string;
  idToken: string;
}

function cloudHeaders(ctx?: CloudContext): Record<string, string> {
  if (!ctx) return {};
  return {
    'eft-region': ctx.region,
    'eft-endpoint': ctx.endpoint,
    'eft-issuer': ctx.issuer,
    ...(ctx.identityId ? { 'eft-identity-id': ctx.identityId } : {}),
    'eft-id-token': ctx.idToken,
  };
}

/** A poll that has not answered in this long is treated as a miss and retried. */
const POLL_TIMEOUT_MS = 4000;

async function req<T>(
  base: string, method: string, path: string, body?: unknown, ctx?: CloudContext,
  /**
   * Abandon the request after this long. Off by default: most calls here are cheap, and a capture
   * legitimately waits on a human presenting a finger.
   *
   * It exists for the POLL, which had no bound at all. A Device Service that stopped answering left the
   * awaiting promise pending forever, and the capture loop only re-checks its cancel and skip flags at the
   * TOP of an iteration — so it never reached them. `capturing` never cleared, and every card and mark
   * button on the grid is disabled while it is set, so one unanswered poll disabled the whole screen with
   * no way back short of a reload.
   */
  timeoutMs?: number,
): Promise<T> {
  const headers: Record<string, string> = { ...DEVSVC_HEADERS, ...cloudHeaders(ctx) };
  if (body !== undefined) headers['content-type'] = 'application/json';
  const ctrl = timeoutMs ? new AbortController() : undefined;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : undefined;
  const res = await dsFetch(`${base}${path}`, {
    method,
    headers,
    ...(ctrl ? { signal: ctrl.signal } : {}),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }).finally(() => { if (timer) clearTimeout(timer); });
  if (!res.ok) throw new Error(`Device Service ${method} ${path} -> ${res.status}`);
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export interface DeviceInfo {
  deviceId: string | number;
  deviceIndex?: number;
  deviceName: string;
  deviceSerial: string;
  deviceVersion?: string;
  softwareVersion?: string;
  vendorName?: string | null;
}

export interface EbtsResult {
  ebtsFile: string; // base64
  transactionControlNumber: string;
  typeOfTransaction: string;
  warning?: string[];
  violations?: string[];
}

export interface ImageDataDto {
  data?: string; // base64 image
  complete?: boolean;
  status?: string;
  warning?: string;
  error?: string;
  dpi?: number;
}

/** CaptureController's FingerprintDTO — the shape /captures/stitch takes and returns. */
export interface FingerprintDto {
  fgp?: number;
  ampCode?: string;
  image?: { mime?: string; data?: string };
  width?: number;
  height?: number;
  dpi?: number;
}

export interface DetectedFinger {
  position?: string;
  quality?: number; // NFIQ
}

/** High-level Device Service API. */
export class DeviceService {
  private constructor(readonly base: string) {}

  static async connect(): Promise<DeviceService> {
    return new DeviceService(await discoverBaseUrl());
  }

  /** Uses whichever /version contract discovery established for this install, rather than assuming. */
  version(): Promise<string> {
    return dsFetch(`${this.base}/version`, cachedVersionNeedsHeaders ? { headers: DEVSVC_HEADERS } : {})
      .then((r) => r.text());
  }

  /** "32" or "64" — the Device Service process architecture. */
  bits(): Promise<string> {
    return dsFetch(`${this.base}/bits`, { headers: DEVSVC_HEADERS }).then((r) => r.text());
  }

  /** Installed device-library version for a scanner vendor (empty/none if not installed). */
  async libraryInstalled(vendor: string): Promise<string | null> {
    try {
      const v = await dsFetch(`${this.base}/device/finger/library-installed/${encodeURIComponent(vendor)}`, { headers: DEVSVC_HEADERS }).then((r) => r.text());
      return v && v !== 'null' ? v : null;
    } catch {
      return null;
    }
  }

  /** Whether a newer device library is available to install for a vendor. */
  async libraryAvailable(vendor: string): Promise<boolean> {
    try {
      const v = await dsFetch(`${this.base}/device/finger/library-available/${encodeURIComponent(vendor)}`, { headers: DEVSVC_HEADERS }).then((r) => r.text());
      return v === 'true';
    } catch {
      return false;
    }
  }

  /** The list endpoint returns [{ deviceIndex, deviceInfo }]; unwrap to DeviceInfo carrying its index. */
  async listScanners(refresh = false): Promise<DeviceInfo[]> {
    const raw = await req<{ deviceIndex: number; deviceInfo: DeviceInfo }[]>(
      this.base,
      'GET',
      `/device/finger/list?refresh=${refresh}`,
    );
    return (raw ?? []).map((d) => ({ ...d.deviceInfo, deviceIndex: d.deviceIndex }));
  }

  /** Init/claim a scanner by its deviceIndex (from listScanners). */
  initScanner(deviceIndex: number): Promise<void> {
    return req(this.base, 'POST', `/device/finger/init/${deviceIndex}`);
  }

  deinitScanner(): Promise<void> {
    return req(this.base, 'POST', '/device/finger/deinit');
  }

  /** Start a friction-ridge capture for a scan position (e.g. ROLLED_RIGHT_THUMB, RIGHT_FOUR). */
  startCapture(scanType: string): Promise<void> {
    return dsFetch(`${this.base}/device/finger/capture`, {
      method: 'POST',
      headers: { ...DEVSVC_HEADERS, 'content-type': 'application/x-www-form-urlencoded' },
      body: `frsType=${encodeURIComponent(scanType)}`,
    }).then((r) => {
      if (!r.ok) throw new Error(`capture start failed: ${r.status}`);
    });
  }

  /**
   * Poll the current preview/result frame. `complete` flips true when the capture finishes.
   *
   * Bounded, unlike the rest of this client. The caller polls in a tight loop and already treats a throw as
   * a transient miss, so a slow answer costs one iteration — while an answer that never came used to wedge
   * the capture screen entirely.
   */
  pollImage(): Promise<ImageDataDto> {
    return req<ImageDataDto>(this.base, 'GET', '/device/finger/image', undefined, undefined, POLL_TIMEOUT_MS);
  }

  cancelCapture(): Promise<void> {
    return req(this.base, 'POST', '/device/finger/cancel');
  }

  /**
   * Best-effort scanner release for teardown: cancel any in-progress capture and deinit the scanner so
   * the next session can claim it. A scanner left initialized is what puts Crossmatch units into the
   * "in use by another application" busy state.
   *
   * Both requests are dispatched synchronously and sent with `keepalive` so they survive the document
   * being torn down on a tab close or refresh, where an ordinary fetch is cancelled mid-flight. Errors
   * are swallowed — this runs while the page is going away and there is nothing left to react to one.
   */
  release(): Promise<void> {
    const opts: LnaRequestInit = { method: 'POST', headers: DEVSVC_HEADERS, keepalive: true };
    const cancel = dsFetch(`${this.base}/device/finger/cancel`, opts).catch(() => undefined);
    const deinit = dsFetch(`${this.base}/device/finger/deinit`, opts).catch(() => undefined);
    return Promise.allSettled([cancel, deinit]).then(() => undefined);
  }

  /**
   * Force the in-progress capture to complete now (DeviceController.forceCapture, "Manual capture").
   *
   * Some scanners never decide the impression is good enough — the operator can see a usable print on the
   * preview while auto-capture keeps waiting. v1 exposed this as a button; without it the only way out is
   * to cancel and lose the impression.
   */
  forceCapture(): Promise<void> {
    return req(this.base, 'POST', '/device/finger/image');
  }

  detectFingers(): Promise<DetectedFinger[]> {
    return req<DetectedFinger[]>(this.base, 'GET', '/device/finger/detect-fingers');
  }

  /**
   * Combine two half-slaps into one four-finger slap image.
   *
   * For two-finger scanners (Watson, DactyScan40i), whose platen cannot take four fingers at once: the
   * operator captures index+middle and ring+little, and the Device Service composites them into the
   * single slap the EBTS expects at FGP 13/14.
   *
   * The wire shape is CaptureController.slapStitch's StitchDTO, not the flat one this first sent: each
   * half is a FingerprintDTO whose bytes live at `image.data`. Passing `{ data }` directly made
   * `dto.getIndexMiddle().getImage()` null, and the resulting NPE escaped the controller's
   * `catch (ImageException)` as an HTTP 500.
   *
   * `mime` is deliberately omitted. The field exists on BinaryDataDTO but the stitch path only ever reads
   * getData(), and Spring's MimeType has no obvious Jackson string binding — sending it risks a
   * deserialization failure for a value the server never looks at.
   *
   * Requires the TWO_FINGER_STITCH feature on the Device Service licence.
   */
  async stitchSlap(
    hand: 'RIGHT_HAND' | 'LEFT_HAND',
    indexMiddle: { data: string; dpi?: number | undefined },
    ringLittle: { data: string; dpi?: number | undefined },
  ): Promise<{ data?: string } | null> {
    const half = (h: { data: string; dpi?: number | undefined }) => ({
      ...(h.dpi ? { dpi: h.dpi } : {}),
      image: { data: h.data },
    });
    const res = await req<FingerprintDto>(this.base, 'POST', '/captures/stitch', {
      hand,
      indexMiddle: half(indexMiddle),
      ringLittle: half(ringLittle),
    });
    // FingerprintDTO, so the stitched bytes come back nested too.
    return res?.image?.data ? { data: res.image.data } : null;
  }

  /** Build the EBTS/EFT from a CustomEftDto (or captureId). Requires cloud context for licensing. */
  buildCustomEft(dto: unknown, ctx: CloudContext): Promise<EbtsResult> {
    return req<EbtsResult>(this.base, 'POST', '/captures/eft', dto, ctx);
  }

  /** Parse an uploaded .eft back into a CaptureDTO (corrections). */
  readEft(captureId: string, dto: unknown, ctx: CloudContext): Promise<unknown> {
    return req(this.base, 'POST', `/captures/${captureId}/read`, dto, ctx);
  }

  /**
   * Open the Device Service's own print window: it asks the operator to pick an .eft file on this
   * workstation and prints it as an FD-258 card or letter sheet.
   *
   * The whole interaction happens on the desktop. The controller answers 204 as soon as it has handed the
   * job to the JavaFX thread, so this resolves when the window has been ASKED for — not when anything is
   * printed, and not if the operator cancels.
   *
   * The path variable is ignored — CaptureController.submitPrintJob never resolves a capture type from it —
   * so "0" is passed rather than inventing an id. The mapping only exists from MIN_PRINT_VERSION; older
   * builds have no route there and answer 404.
   */
  printEft(): Promise<void> {
    return req<void>(this.base, 'POST', '/captures/0/print');
  }
}
